import type { DurationLike } from 'luxon';
import { DateTime } from 'luxon';

export enum FilterKey {
  StartDate = 'startDate',
  EndDate = 'endDate',
  Value = 'value',
  Interval = 'interval',
}

export enum IntervalUnits {
  Seconds = 'seconds',
  Minutes = 'minutes',
  Hours = 'hours',
}

export type SettableFilterKey = Exclude<FilterKey, FilterKey.Interval>;

export type Updater<T> = T | ((prev: T) => T);

const min = 60;
const hour = min * 60;
const day = hour * 24;
const month = 30 * day;

export enum TimePeriod {
  FiveMinutes = 5 * min,
  TenMinutes = 10 * min,
  ThirtyMinutes = 30 * min,
  OneHour = hour,
  TwoHours = 2 * hour,
  ThreeHours = 3 * hour,
  SixHours = 6 * hour,
  TwelveHours = 12 * hour,
  OneDay = day,
  SevenDays = 7 * day,
  ThirtyDays = 30 * day,
  ThreeMonths = 3 * month,
  SixMonths = 6 * month,
  NineMonths = 9 * month,
  OneYear = 12 * month,
  Custom = 'CUSTOM',
}

// eslint-disable-next-line consistent-return
export function timePeriodLabel(period: TimePeriod): string {
  switch (period) {
    case TimePeriod.Custom:
      return 'CUSTOM';
    case TimePeriod.FiveMinutes:
      return 'Last 5 minutes';
    case TimePeriod.TenMinutes:
      return 'Last 10 minutes';
    case TimePeriod.ThirtyMinutes:
      return 'Last 30 minutes';
    case TimePeriod.OneHour:
      return 'Last hour';
    case TimePeriod.TwoHours:
      return 'Last 2 hours';
    case TimePeriod.ThreeHours:
      return 'Last 3 hours';
    case TimePeriod.SixHours:
      return 'Last 6 hours';
    case TimePeriod.TwelveHours:
      return 'Last 12 hours';
    case TimePeriod.OneDay:
      return 'Last 24 hours';
    case TimePeriod.SevenDays:
      return 'Last 7 days';
    case TimePeriod.ThirtyDays:
      return 'Last 30 days';
    case TimePeriod.ThreeMonths:
      return 'Last 3 months';
    case TimePeriod.SixMonths:
      return 'Last 6 months';
    case TimePeriod.NineMonths:
      return 'Last 9 months';
    case TimePeriod.OneYear:
      return 'Last year';
  }
}

// eslint-disable-next-line consistent-return
export function timePeriodLabelFromString(period: string): string {
  switch (period) {
    case '5':
      return 'Last 5 minutes';
    case '10':
      return 'Last 10 minutes';
    case '30':
      return 'Last 30 minutes';
    case '60':
      return 'Last hour';
    case '120':
      return 'Last 2 hours';
    case '180':
      return 'Last 3 hours';
    case '360':
      return 'Last 6 hours';
    case '720':
      return 'Last 12 hours';
    case '1440':
      return 'Last 24 hours';
    case '10080':
      return 'Last 7 days';
    case '43200':
      return 'Last 30 days';
    default:
      return '';
  }
}

export function getStep(currentTimePeriod: number, defaultStepSeconds: number = 120): number {
  if (currentTimePeriod >= TimePeriod.ThreeMonths) return TimePeriod.OneDay;
  if (currentTimePeriod >= TimePeriod.ThirtyDays) return TimePeriod.ThreeHours;
  if (currentTimePeriod >= TimePeriod.SevenDays) return TimePeriod.OneHour;
  if (currentTimePeriod >= TimePeriod.OneDay) return TimePeriod.TenMinutes;
  if (currentTimePeriod >= TimePeriod.SixHours) return 2 * min;

  return defaultStepSeconds;
}

export const defaultIntervalSeconds = TimePeriod.OneHour;
export const defaultIntervalUnits = IntervalUnits.Seconds;
export const maxDuration = 7776000;

export function getIntervalFromNow(duration: DurationLike): number {
  return DateTime.now().startOf('day').minus(duration).toSeconds();
}

// Using types to ensure consistent return
// eslint-disable-next-line consistent-return
export function getDuration(
  currentTimePeriod: TimePeriod,
  units: IntervalUnits = IntervalUnits.Seconds,
): number {
  if (currentTimePeriod === TimePeriod.Custom) return 0;

  switch (units) {
    case IntervalUnits.Seconds:
      return currentTimePeriod;
    case IntervalUnits.Minutes:
      return currentTimePeriod / min;
    case IntervalUnits.Hours:
      return currentTimePeriod / hour;
  }
}
